const API_BASE = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : '/api';

function getToken() {
  return localStorage.getItem('cm_token');
}

async function request(path, options = {}) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  
  if (res.status === 401) {
    localStorage.removeItem('cm_token');
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }
  
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

export const api = {
  // Auth
  verifyToken: () => request('/auth/verify', { method: 'POST' }),

  // Classes
  getClasses: () => request('/classes'),
  getClass: (id) => request(`/classes/${id}`),
  createClass: (data) => request('/classes', { method: 'POST', body: JSON.stringify(data) }),
  updateClass: (id, data) => request(`/classes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteClass: (id) => request(`/classes/${id}`, { method: 'DELETE' }),

  // Students
  getStudents: () => request('/students'),
  getStudent: (id) => request(`/students/${id}`),
  createStudent: (data) => request('/students', { method: 'POST', body: JSON.stringify(data) }),
  updateStudent: (id, data) => request(`/students/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteStudent: (id) => request(`/students/${id}`, { method: 'DELETE' }),
  toggleStudent: (id) => request(`/students/${id}/toggle`, { method: 'PATCH' }),
  enrollStudent: (id, data) => request(`/students/${id}/enroll`, { method: 'POST', body: JSON.stringify(data) }),
  unenrollStudent: (id, classId) => request(`/students/${id}/enroll/${classId}`, { method: 'DELETE' }),

  // Attendance
  getSessions: (startDate, endDate) => request(`/attendance/sessions?start_date=${startDate}&end_date=${endDate}`),
  getSession: (id) => request(`/attendance/sessions/${id}`),
  createSession: (data) => request('/attendance/sessions', { method: 'POST', body: JSON.stringify(data) }),
  deleteSession: (id) => request(`/attendance/sessions/${id}`, { method: 'DELETE' }),
  getDateSessions: (date) => request(`/attendance/date/${date}`),

  // Payments
  getPayments: (studentId) => request(`/payments${studentId ? `?student_id=${studentId}` : ''}`),
  createPayment: (data) => request('/payments', { method: 'POST', body: JSON.stringify(data) }),
  updatePayment: (id, data) => request(`/payments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deletePayment: (id) => request(`/payments/${id}`, { method: 'DELETE' }),

  // Reports
  getDashboard: () => request('/reports/dashboard'),
  getBalances: () => request('/reports/balances'),
  getStudentReport: (id) => request(`/reports/student/${id}`),

  // Scheduled Events
  getScheduledEvents: (startDate, endDate) => request(`/schedule?start_date=${startDate}&end_date=${endDate}`),
  getScheduledEventsForDate: (date) => request(`/schedule/date/${date}`),

  // Predictions
  getPredictions: (date, tz) => request(`/predictions?date=${date}&tz=${encodeURIComponent(tz)}`),
  createScheduledEvent: (data) => request('/schedule', { method: 'POST', body: JSON.stringify(data) }),
  deleteScheduledEvent: (id) => request(`/schedule/${id}`, { method: 'DELETE' }),

  // Recordings
  getRecordingUploadUrl: (data) => request('/recordings/upload-url', { method: 'POST', body: JSON.stringify(data) }),
  getRecordingsBySession: (sessionId) => request(`/recordings/session/${sessionId}`),
  getRecordingsByClass: (classId) => request(`/recordings/class/${classId}`),
  deleteRecording: (id) => request(`/recordings/${id}`, { method: 'DELETE' }),
  transcribeRecording: (id) => request(`/recordings/${id}/transcribe`, { method: 'POST' }),

  // Direct S3 upload using presigned URL (no auth header — goes straight to S3)
  uploadToS3: async (presignedUrl, file) => {
    const res = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'audio/mp4' },
    });
    if (!res.ok) throw new Error('Upload to S3 failed');
  },
};
